/* Multi-cup Studio forms use the shared durable cart journal. This adapter
 * never stores artwork or performs requests. Keep the prepared forms for an
 * explicit retry; recovery only checks the cart and never replays uploads. */
(function (root) {
  'use strict';
  if (root.JELivePersonalizeSet) return;
  const failure = (code, ambiguous = false) => Object.assign(new Error(code), { code, ambiguous });
  const ID = /^[1-9]\d{0,23}$/;
  const STUDIO_TOKEN = 'properties[_Studio submission]';
  function create(config = {}) {
    if (!root.JEBirthflowerCart?.create) throw failure('cart_unavailable');
    const transport = root.JEBirthflowerCart.create({ root: config.root, productId: config.productId, kind: 'live-personalize' });
    let frozen = null, busy = false;

    function inspect() {
      const receipt = transport.inspect();
      const confirmedIndexes = [], unfinishedIndexes = [];
      receipt?.rows.forEach((row, index) => (row.status === 'confirmed' ? confirmedIndexes : unfinishedIndexes).push(index));
      const ownsPending = Boolean(receipt?.pending && frozen && receipt.rows.length === frozen.length &&
        receipt.rows.every((row, index) => ['token', 'variantId', 'quantity'].every(key => row[key] === frozen[index].prepared[key])));
      return { ...receipt, hasPending: Boolean(receipt?.pending), complete: Boolean(receipt?.complete),
        uncertain: Boolean(receipt?.uncertain), rows: receipt?.rows || [], cartUrl: transport.cartUrl,
        confirmedIndexes, unfinishedIndexes, ownsPending,
        canContinue: Boolean(receipt?.pending && !receipt.uncertain && unfinishedIndexes.length &&
          receipt.rows.every(row => ['ready', 'rejected', 'confirmed'].includes(row.status))),
        canRetry: Boolean(ownsPending && !receipt.uncertain &&
          receipt.rows.every(row => ['ready', 'rejected', 'confirmed'].includes(row.status))) };
    }
    function snapshot(items) {
      if (!Array.isArray(items) || !items.length || items.length > 24) throw failure('cart_invalid');
      let total = 0;
      const rows = items.map(item => {
        if (!(item?.form instanceof FormData) || !ID.test(String(item.variantId)) ||
            typeof item.variantId === 'number' && !Number.isSafeInteger(item.variantId) ||
            !Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > 99) throw failure('cart_invalid');
        const variantId = String(item.variantId), entries = Array.from(item.form);
        if (item.form.get('id') !== variantId || item.form.get('quantity') !== String(item.quantity) ||
            item.form.has(STUDIO_TOKEN)) throw failure('cart_invalid');
        total += item.quantity;
        return { original: item.form, entries, variantId, quantity: item.quantity };
      });
      if (total > 100) throw failure('cart_invalid');
      return rows;
    }
    function sameRows(left, right) {
      return left.length === right.length && left.every((row, index) => {
        const other = right[index];
        return row.original === other.original && row.variantId === other.variantId && row.quantity === other.quantity &&
          row.entries.length === other.entries.length && row.entries.every(([key, value], n) =>
            key === other.entries[n][0] && value === other.entries[n][1]);
      });
    }
    function freezeRows(rows) {
      return rows.map(row => {
        const form = new FormData(), token = crypto.randomUUID();
        for (const [key, value] of row.entries) form.append(key, value);
        form.set(STUDIO_TOKEN, token);
        return { ...row, prepared: { form, token, variantId: row.variantId, quantity: row.quantity } };
      });
    }
    function completed(result) {
      if (result?.complete) return { ...result, complete: true, cartUrl: transport.cartUrl };
      if (!result) return null;
      const rejected = !result.uncertain && result.rows?.some(row => row.status === 'rejected');
      throw Object.assign(failure(rejected ? 'cart_rejected' : 'cart_unconfirmed', !rejected),
        { result, cartUrl: transport.cartUrl });
    }
    async function exclusive(action) {
      if (busy) throw failure('cart_busy', true);
      busy = true;
      try { return await action(); }
      catch (error) { error.cartUrl ||= transport.cartUrl; throw error; }
      finally { busy = false; }
    }
    function submit(items, onProgress) {
      return exclusive(() => {
        // Freeze every form before addBatch's first await; caller mutation
        // cannot change the set currently being uploaded.
        const state = inspect(), rows = snapshot(items);
        if (state.hasPending || state.complete) {
          if (!frozen || !sameRows(frozen, rows)) throw failure('cart_batch_conflict', state.hasPending);
        } else if (!frozen || !sameRows(frozen, rows)) frozen = freezeRows(rows);
        return transport.addBatch(frozen.map(row => row.prepared), { onProgress }).then(completed);
      });
    }
    function recover(onProgress) {
      return exclusive(() => transport.recover({ onProgress }).then(completed));
    }
    function clearCompleted() {
      if (busy) throw failure('cart_busy', true);
      transport.clearCompleted();
      frozen = null;
    }
    function continueUnfinished(expectedRows) {
      // Explicit shopper acknowledgement only. The UI must match its restored
      // draft by row order before removing confirmed cups from that draft.
      return exclusive(async () => {
        const state = inspect();
        if (!state.canContinue) throw failure('cart_unconfirmed', state.hasPending);
        if (!Array.isArray(expectedRows) || expectedRows.length !== state.rows.length ||
            expectedRows.some((row, index) => String(row?.variantId) !== state.rows[index].variantId || row?.quantity !== state.rows[index].quantity)) {
          throw failure('cart_batch_conflict', true);
        }
        // This canonical operation rechecks every confirmed cart line, refuses
        // unknown writes, and clears only the journal; it never changes a line.
        const result = await transport.abandonChecked();
        if (!result?.abandoned) throw failure('cart_batch_conflict', true);
        const confirmedIndexes = [], unfinishedIndexes = [];
        result.rows.forEach((row, index) => (row.status === 'confirmed' ? confirmedIndexes : unfinishedIndexes).push(index));
        frozen = null;
        return { ...result, continued: true, confirmedIndexes,
          unfinishedIndexes, cartUrl: transport.cartUrl };
      });
    }
    return Object.freeze({ inspect, submit, recover, clearCompleted, continueUnfinished });
  }
  root.JELivePersonalizeSet = Object.freeze({ create, limits: Object.freeze({ rows: 24, quantity: 99, total: 100 }) });
})(typeof globalThis !== 'undefined' ? globalThis : window);
